// src/components/ProjectDetails.js

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import StaticAnalysis, {
  ChartDataProvider,
} from "./projectDetails/StaticAnalysis";
import ProjectBrief from "./projectDetails/ProjectBrief";
import UnitTesting from "./projectDetails/UnitTesting";
import StyledTab from "./common/StyledTab";
import MemoryLeak from "./projectDetails/MemoryLeak";
import IntegrationTesting from "./projectDetails/IntegrationTesting";
import CybersecurityTesting from "./projectDetails/CybersecurityTesting";
import FOSSCompliance from "./projectDetails/FOSSCompliance";
import UpgradePage from "./Upgrade";
import UpgradePageForTab from "./UpgradePage";
import AiAnalysis from "./projectDetails/AiAnalysis";
import Layout from "./Layout";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-toastify";

const ProjectDetails = () => {
  const { organizationId, planId } = useAuth();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const newRunId = useRef(uuidv4());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("access_token");
  const [tools, setTools] = useState({
    buildTool: [],
    staticAnalysisTools: [],
    unitTestingTools: [],
    memoryTestingTools: [],
    cybersecurityAnalysisTools: [],
    fossComplianceTools: [],
  });
  const analysisResult = location.state?.analysisResult ?? null;
  const theme = useTheme();
  const fetchProjectDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch project details");
      }
      const data = await response.json();
      setProject(data);
    } catch (error) {
      console.error("Error fetching project details:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  useEffect(() => {
    // Fetch all tools (this endpoint is used for stage configuration too)
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/tools-by-plan?plan_id=${planId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch tools");
        }
        return response.json();
      })
      .then((data) => {
        // Separate tools by their type for stage configuration (for org type "O")
        const buildTool = data.filter((tool) => tool.toolType === "buildTool");
        const staticAnalysisTools = data.filter(
          (tool) => tool.toolType === "staticAnalysisTool"
        );
        const unitTestingTools = data.filter(
          (tool) => tool.toolType === "unitTestingTool"
        );
        const memoryTestingTools = data.filter(
          (tool) => tool.toolType === "memoryTestingTool"
        );
        const cybersecurityAnalysisTools = data.filter(
          (tool) => tool.toolType === "cyberSecurityAnalysisTool"
        );
        const fossComplianceTools = data.filter(
          (tool) => tool.toolType === "fossComplianceTool"
        );

        setTools({
          buildTool,
          staticAnalysisTools,
          unitTestingTools,
          memoryTestingTools,
          cybersecurityAnalysisTools,
          fossComplianceTools
        });
        // // For organization type "I", filter tools that have genericAvailability set to true
        // if (organizationType === "I") {
        //   const availableGeneric = data.filter(
        //     (tool) =>
        //       tool.genericAvailability === true ||
        //       tool.genericAvailability === "true"
        //   );
        //   setGenericTools(availableGeneric);
        // }
      })
      .catch((err) => {
        console.error("Error fetching tools:", err);
        setError("Error loading tools. Please try again later.");
      });
  }, [token]);

  useEffect(() => {
    console.log("Location state on ProjectDetails load:", location.state);
    setActiveTab(location.state?.activeTab ?? 0); // Fallback to 0 if activeTab is null or undefined
  }, [location.state]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRunClick = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    console.log("context org id : " + organizationId);
    const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/run`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          project_id: projectId,
          run_id: newRunId.current,
          organization_id: organizationId,
          jwt_access_token: token,
        }),
      });
      const data = await response.json();
      // ✅ Show toast if notification_message exists
      if (data.notification_message) {
        toast.info(data.notification_message);
      }
      if (response.ok && data.success == true) {
        navigate(`/pipeline/${newRunId.current}`);
      } else {
        console.error("Failed to make the POST request");
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error during the POST request", error);
    }
  };

  const handleEditClick = () => navigate(`/projects/${projectId}/edit`);
  const handleDeleteClick = async () => {
    if (window.confirm("Confirm Delete Project?")) {
      await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      navigate("/projects");
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Failed to load data or Project not found. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              rowGap: 2,
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontSize: {
                  xs: "1.25rem",
                  sm: "1.5rem",
                  md: "1.75rem",
                },
                wordBreak: "break-word",
              }}
            >
              {project?.name || "Loading..."}
            </Typography>
            {/* <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                className="athrv-dash-primary-button"
                color="primary"
                disabled={isSubmitting}
                onClick={handleRunClick}
                sx={{ marginRight: 2 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "lightgray" }} />
                ) : (
                  "RUN"
                )}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditClick}
                sx={{
                  marginRight: 2,
                  color: theme.palette.mode === "dark" ? "#fff" : "#fff",
                  borderColor: "#0C1A32",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#282828" : "#484848",
                  "&:hover": {
                    color: "#fff",
                    backgroundColor: "#0C1A32",
                    borderColor: "#002244",
                  },
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
            </Box> */}
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 3,
                  "& .MuiTabs-indicator": {
                    height: "3px",
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                <StyledTab label="Project Details" active={activeTab === 0} />
                <StyledTab label="AI Analysis" active={activeTab === 1} />
                <StyledTab label="Static Analysis" active={activeTab === 2} />
                <StyledTab label="Unit Testing" active={activeTab === 3} />
                <StyledTab label="Integration Testing" active={activeTab === 4} />
                <StyledTab label="Memory Testing" active={activeTab === 5} />
                <StyledTab label="Cybersecurity Analysis" active={activeTab === 6} />
                <StyledTab label="FOSS Compliance" active={activeTab === 7} />
              </Tabs>
              {activeTab === 0 && <ProjectBrief project={project} />}
              {activeTab === 1 && (
                <AiAnalysis analysisResult={analysisResult} />
              )}
              {activeTab === 2 &&
                (tools.staticAnalysisTools.length > 0 ? (
                  <ChartDataProvider projectId={projectId}>
                    <StaticAnalysis />
                  </ChartDataProvider>
                ) : (
                  <UpgradePageForTab />
                ))}
              {activeTab === 3 &&
                (tools.unitTestingTools.length > 0 ? (
                  <UnitTesting />
                ) : (
                  <UpgradePageForTab />
                ))}
              {activeTab === 4 && <UpgradePage />}
              {activeTab === 5 &&
                (tools.memoryTestingTools.length > 0 ? (
                  <MemoryLeak />
                ) : (
                  <UpgradePageForTab />
                ))}

              {activeTab === 6 &&
                (tools.cybersecurityAnalysisTools.length > 0 ? (
                  <CybersecurityTesting />
                ) : (
                  <UpgradePageForTab />
                ))}
              {activeTab === 7 &&
                (tools.fossComplianceTools.length > 0 ? (
                  <FOSSCompliance />
                ) : (
                  <UpgradePageForTab />
                ))}
            </>
          )}
        </Paper>
      </Box>
    </Layout>
  );
};

export default ProjectDetails;
